// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 62.50.1 (user-validation walk, W2) — the one date format the product
 * shows: `dd/MM/yyyy` for a date and `dd/MM/yyyy HH:mm` for a date-time, in
 * every locale. The frontend's `formatDate` / `formatDateTime`
 * (`src/frontend/src/utils/dateFormat.ts`) render through these, and a server
 * document that prints what a page shows (the supervision-register exports)
 * calls them too, so a page and its export never write one date two ways.
 *
 * - {@link displayCalendarDate}: a stored `YYYY-MM-DD` is REARRANGED as text and
 *   never parsed through `Date`, which would attach a timezone the record does
 *   not carry. A value that is not a real calendar date returns `null`.
 * - {@link displayFrameworkDate}: a framework record's date at the precision the
 *   record states it (`YYYY`, `YYYY-MM` or `YYYY-MM-DD`), rearranged as text the
 *   same way. Anything else returns `null`.
 * - {@link displayDate} / {@link displayDateTime}: an instant's wall clock in
 *   `timeZone` (an IANA zone), or in the runtime's own zone when none is given.
 *   An unknown zone returns `null`, so the caller decides the fallback.
 */
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const pad = (n, width = 2) => String(n).padStart(width, '0');
/** `dd/MM/yyyy` of a `YYYY-MM-DD` that exists on the calendar; `null` otherwise. */
export function displayCalendarDate(value) {
    const m = CALENDAR_DATE.exec(value);
    if (!m)
        return null;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    if (month < 1 || month > 12 || day < 1)
        return null;
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    if (day > daysInMonth)
        return null;
    return `${m[3]}/${m[2]}/${m[1]}`;
}
const FRAMEWORK_YEAR = /^(\d{4})$/;
const FRAMEWORK_YEAR_MONTH = /^(\d{4})-(\d{2})$/;
/**
 * A framework record's date (`effective_date`, `effective_end`, a framework
 * activation's date) at the precision the record states it, never more:
 * `YYYY` reads `yyyy`, `YYYY-MM` reads `MM/yyyy`, and `YYYY-MM-DD` reads
 * `dd/MM/yyyy` through {@link displayCalendarDate}. `null` for anything else
 * (a month outside 01–12, a day the calendar does not have, an instant, an
 * absent value), so the caller decides the fallback.
 *
 * I-1, gate E12.5 pass 3 (engineering P3-5): a pack dates a record only as
 * precisely as its source does (GS1 dates GTS 2.0 "August 2017", with no day).
 * The frontend's `formatFrameworkDate` and a server document that prints the
 * same date (the data-exploitation export) both read this, so a page and its
 * export never write one framework date two ways.
 */
export function displayFrameworkDate(value) {
    if (!value)
        return null;
    const year = FRAMEWORK_YEAR.exec(value);
    if (year)
        return year[1];
    const yearMonth = FRAMEWORK_YEAR_MONTH.exec(value);
    if (yearMonth) {
        const month = Number(yearMonth[2]);
        return month >= 1 && month <= 12 ? `${yearMonth[2]}/${yearMonth[1]}` : null;
    }
    return displayCalendarDate(value);
}
function wallClock(d, timeZone) {
    if (Number.isNaN(d.getTime()))
        return null;
    if (!timeZone) {
        return {
            year: pad(d.getFullYear(), 4),
            month: pad(d.getMonth() + 1),
            day: pad(d.getDate()),
            hour: pad(d.getHours()),
            minute: pad(d.getMinutes()),
        };
    }
    let parts;
    try {
        parts = new Intl.DateTimeFormat('en-GB', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
        }).formatToParts(d);
    }
    catch {
        return null;
    }
    const part = (type) => parts.find((p) => p.type === type)?.value ?? '';
    return {
        year: part('year'),
        month: part('month'),
        day: part('day'),
        hour: part('hour'),
        minute: part('minute'),
    };
}
/** `dd/MM/yyyy` of `d` in `timeZone` (the runtime's zone when omitted); `null` for an invalid date or unknown zone. */
export function displayDate(d, timeZone) {
    const c = wallClock(d, timeZone);
    return c ? `${c.day}/${c.month}/${c.year}` : null;
}
/** `dd/MM/yyyy HH:mm` of `d` in `timeZone` (the runtime's zone when omitted); `null` for an invalid date or unknown zone. */
export function displayDateTime(d, timeZone) {
    const c = wallClock(d, timeZone);
    return c ? `${c.day}/${c.month}/${c.year} ${c.hour}:${c.minute}` : null;
}
const LOCAL_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;
/**
 * Sprint 63.31.0 (browser re-verification, defect 1) — an instant as a
 * `<input type="datetime-local">` shows it **in `timeZone`** (`YYYY-MM-DDTHH:mm`),
 * for a form whose values are the organisation's own time rather than the
 * browser's. `null` for an invalid date or unknown zone. The inverse is
 * {@link zonedLocalInputToInstant}.
 */
export function instantToZonedLocalInput(d, timeZone) {
    const c = wallClock(d, timeZone);
    return c ? `${c.year}-${c.month}-${c.day}T${c.hour}:${c.minute}` : null;
}
/**
 * A wall-clock `YYYY-MM-DDTHH:mm[:ss]` read **in `timeZone`**, as an ISO
 * instant. Two passes settle the zone's offset across a daylight-saving change.
 * `null` for a value that is not a real date-time, or an unknown zone.
 */
export function zonedLocalInputToInstant(local, timeZone) {
    const m = LOCAL_DATE_TIME.exec(local);
    if (!m)
        return null;
    const [year, month, day, hour, minute] = [m[1], m[2], m[3], m[4], m[5]].map(Number);
    const second = m[6] === undefined ? 0 : Number(m[6]);
    if (displayCalendarDate(`${m[1]}-${m[2]}-${m[3]}`) === null)
        return null;
    if (hour > 23 || minute > 59 || second > 59)
        return null;
    const target = Date.UTC(year, month - 1, day, hour, minute, second);
    let guess = target;
    for (let pass = 0; pass < 2; pass += 1) {
        const c = wallClock(new Date(guess), timeZone);
        if (!c)
            return null;
        const seen = Date.UTC(Number(c.year), Number(c.month) - 1, Number(c.day), Number(c.hour), Number(c.minute)) +
            (((guess % 60_000) + 60_000) % 60_000);
        guess += target - seen;
    }
    return new Date(guess).toISOString();
}
//# sourceMappingURL=display-date.js.map